import { useEffect } from "react";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useNavigate } from "react-router-dom";

/**
 * Deep link handler for Capacitor app.
 *
 * Goal: ANY link like
 *  - https://app.siriuscapital.am/auth/login
 *  - https://app.siriuscapital.am/
 *  - https://app.siriuscapital.am/home?foo=1
 * should open the app and navigate to the same route in React Router.
 *
 * Also supports your legacy format:
 *  - https://app.siriuscapital.am/open?path=/wallet&foo=1
 */
export function useDeepLink() {
  const navigate = useNavigate();

  useEffect(() => {
    // 1) Only run inside native app (iOS/Android). On web/PWA this should do nothing.
    if (!Capacitor.isNativePlatform()) return;

    // 2) This function runs whenever the app receives a universal/app link.
    const handleAppUrlOpen = async (event: { url: string }) => {
      try {
        console.log("Deep link received:", event.url);

        // 3) Parse the incoming URL. The base helps if the URL is missing scheme in some edge cases.
        const incoming = new URL(event.url, "https://app.siriuscapital.am");

        // 4) Decide which in-app route to navigate to:
        //    - NEW preferred way: use the real path (/auth/login, /home, /, etc.)
        //    - Legacy support: if link is /open?path=/something, then use that /something instead
        const legacyPath = incoming.searchParams.get("path");
        const targetPath = legacyPath
          ? legacyPath.startsWith("/")
            ? legacyPath
            : `/${legacyPath}`
          : incoming.pathname || "/";

        // 5) Keep query params for the destination route.
        //    If we used legacy ?path=, remove it so it doesn't appear in the final URL.
        const params = new URLSearchParams(incoming.searchParams);
        if (legacyPath) params.delete("path");

        const search = params.toString();
        const targetSearch = search ? `?${search}` : "";

        console.log("Navigating to:", `${targetPath}${targetSearch}`);

        // 6) Navigate inside your React Router app
        navigate(
          { pathname: targetPath, search: targetSearch },
          { replace: true }
        );
      } catch (error) {
        console.error("Error handling deep link:", error);
      }
    };

    // 7) Handles links when app is already running (warm start)
    const listener = App.addListener("appUrlOpen", handleAppUrlOpen);

    // 8) Handles link that launched the app (cold start)
    App.getLaunchUrl().then((result) => {
      if (result?.url) handleAppUrlOpen({ url: result.url });
    });

    // 9) Cleanup listener on unmount
    return () => {
      listener.then((l) => l.remove());
    };
  }, [navigate]);
}

/**
 * Build a normal "any path" link for your domain.
 *
 * Example:
 *  buildDeepLink("/auth/login") -> https://app.siriuscapital.am/auth/login
 *  buildDeepLink("/home", { foo: "1" }) -> https://app.siriuscapital.am/home?foo=1
 */
export function buildDeepLink(
  path: string,
  params: Record<string, string> = {},
  baseUrl: string = "https://app.siriuscapital.am"
): string {
  // Ensure path starts with /
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  const url = new URL(normalizedPath, baseUrl);

  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });

  return url.toString();
}
